import { CSSObject } from '@emotion/react'
import {
  Color,
  cssVar,
  Shadow,
  ShadowElevation,
  Spacing,
  ThemedColors,
  ZIndex,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'
import { WithRequiredTheme } from '@opallabs/lapidary'
import { CLASSNAME as VERSION_HISTORY_CONTROL_CLASSNAME } from './components/OpenVersionsButton'

type ContainerStyleProps = WithRequiredTheme & {
  isFullScreen: boolean
  showVersionHistoryControlOnHover: boolean
}

const STANDARD_WIDTH = '800px'
const FULLSCREEN_WIDTH = '856px'

export const container = ({
  showVersionHistoryControlOnHover,
  theme,
  isFullScreen,
}: ContainerStyleProps): CSSObject => {
  const base: CSSObject = {
    borderRadius: '12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    marginInlineEnd: '16px',
    inlineSize: 'auto',
    display: 'flex',
    flexDirection: 'column',
    ...(isFullScreen ? Shadow[ShadowElevation.E2] : {}),
    backgroundColor: Color[theme].Surface.N000,
    color: Color[theme].Text.Default,
    borderColor: Color[theme].Surface.N300,

    ...(showVersionHistoryControlOnHover
      ? {
          [`&:hover .${VERSION_HISTORY_CONTROL_CLASSNAME}`]: {
            opacity: 1,
          },
        }
      : {}),
  }

  const fullScreenStyle: CSSObject = {
    position: 'fixed',
    insetBlockEnd: 0,
    insetInlineStart: 0,
    inlineSize: '100vi',
    maxInlineSize: '100vi',
    minBlockSize: 'calc(100vb - 40px)',
    maxBlockSize: 'calc(100vb - 40px)',
    margin: 0,
    borderStartStartRadius: '20px',
    borderStartEndRadius: '20px',
    borderEndEndRadius: '0',
    borderEndStartRadius: '0',
    zIndex: ZIndex.Shroud.Content,
  }

  return {
    ...base,
    ...(isFullScreen ? fullScreenStyle : {}),
  }
}

export const contentWrapper = (
  isFullScreen: boolean,
  isSynced?: boolean
): CSSObject => {
  const base: CSSObject = {
    flexGrow: 1,

    // Hides placeholder text until editor provider is synced
    '& .is-editor-empty': {
      display: isSynced ? undefined : 'none',
    },
  }

  const fullScreenStyle: CSSObject = {
    '&:not(:focus)': {
      cursor: 'text',
    },
  }

  return {
    ...base,
    ...(isFullScreen ? fullScreenStyle : {}),
  }
}

export const content = ({
  isFullScreen,
  controlBarPlacement,
}: {
  isFullScreen: boolean
  controlBarPlacement: 'top' | 'bottom'
}): CSSObject => {
  const base: CSSObject = {
    // allow space for tooltip on highlighted text when control bar is at top
    paddingBlockStart: controlBarPlacement === 'bottom' ? Spacing.M : 66,
    paddingBlockEnd: Spacing.M * 2.5,
    paddingInline: Spacing.M,

    // Use :where to avoid increased specificity of :not
    '& > :where(:not(.react-renderer, .tableWrapper))': {
      maxInlineSize: STANDARD_WIDTH,
    },

    '&[contenteditable="false"]': {
      paddingBlockEnd: Spacing.XS * 3,
    },

    '& > :first-child, & > .text-center-justify:first-child > :first-child': {
      marginBlockStart: 0,
    },

    '.tiptap-thread--unresolved': {
      backgroundColor: '#FFECC6',
    },

    '[data-type="mention"]': {
      backgroundColor: Color.light['Color/Primary/Primary-3'],
      borderRadius: Spacing.XS,
      color: Color.light['Text/Links'],
      padding: `${Spacing.XS / 2}px ${Spacing.XS}px`,
    },

    // AI Toolkit review decorations
    '.tiptap-ai-suggestion, .tiptap-ai-suggestion > *': {
      backgroundColor: `color(from ${cssVar(ThemedColors.Error1)} display-p3 r g b / 0.2)`,
      color: cssVar(ThemedColors.TextSecondary),
      textDecoration: 'line-through',
    },
    '.tiptap-ai-suggestion-diff, .tiptap-ai-suggestion-diff > *': {
      backgroundColor: `color(from ${cssVar(ThemedColors.Success1)} display-p3 r g b / 0.2)`,
    },

    '.collaboration-cursor__caret': {
      marginInline: '-1px',
      pointerEvents: 'none',
      position: 'relative',
      wordBreak: 'normal',
      borderInline: '1px solid #0d0d0d',
    },
    '.collaboration-cursor__label': {
      borderStartStartRadius: '3px',
      borderStartEndRadius: '3px',
      borderEndEndRadius: '3px',
      borderEndStartRadius: '0',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 500,
      insetInlineStart: '-1px',
      lineHeight: 'normal',
      paddingBlock: '0.1rem',
      paddingInline: '0.3rem',
      position: 'absolute',
      insetBlockEnd: '100%',
      userSelect: 'none',
      whiteSpace: 'nowrap',
      zIndex: 1,
    },
    '.collaboration-cursor__flipped .collaboration-cursor__label': {
      borderStartStartRadius: '3px',
      borderStartEndRadius: '3px',
      borderEndEndRadius: '0',
      borderEndStartRadius: '3px',
      insetInlineStart: 'auto',
      insetInlineEnd: '-1px',
    },
  }

  const fullScreenStyle: CSSObject = {
    maxInlineSize: FULLSCREEN_WIDTH,
    marginBlockStart: Spacing.XS * 15,
    marginInline: 'auto',
    paddingBlockEnd: '30vb',
    ...modernWithFallback(
      [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
      'overflow-block: auto'
    ),

    '&[contenteditable="false"]': {
      paddingBlockEnd: '12px',
    },

    '.text-center-justify': {
      maxInlineSize: 'inherit',
    },
  }

  return {
    ...base,
    ...(isFullScreen ? fullScreenStyle : {}),
  }
}
